import { get } from 'svelte/store';

import { user_settings } from 'src/stores/account/user';
import { postReq } from 'src/services/http';

import { deletePvSetup } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
import { disableAllNetMetering } from 'src/components/Chart/chart_net_metering_helpers';

import {
	CLONE_SETTINGS_DISCORD_LINK,
	MATH_IN_SCENES_DISCORD_LINK,
	NET_METERING_DISCORD_LINK,
	PV_SETUP_DISCORD_LINK,
	SCRIPT_SCENES_DISCORD_LINK,
} from 'src/constants';

export type ExperimentalFeatureId = 'inf2' | 'mis' | 'cs' | 'nm' | 'pvs';

export type ExperimentalFeatureMeta = {
	id: ExperimentalFeatureId;
	nameKey: string;
	nameDefault: string;
	descriptionKey: string;
	descriptionDefault: string;
	icon: string;
	feedbackLink?: string;
};

/**
 * Catalog of experimental feature toggles shown under User Settings → Labs →
 * Experimental features. Kept here (rather than inline in a component) so the
 * legacy `ExperimentalFeatureRow` and the v2 `ExperimentalFeaturesV2` panel
 * can share one source of truth.
 */
export const experimentalFeatures: Record<ExperimentalFeatureId, ExperimentalFeatureMeta> = {
	inf2: {
		id: 'inf2',
		nameKey: 'feature_inf2_name',
		nameDefault: 'Script Scenes',
		descriptionKey: 'feature_inf2_description',
		descriptionDefault: 'Convert and edit scenes using our new editor with Javascript.',
		feedbackLink: SCRIPT_SCENES_DISCORD_LINK,
		icon: 'fa-light fa-code',
	},
	mis: {
		id: 'mis',
		nameKey: 'label_math_in_scenes',
		nameDefault: 'Math in scenes',
		descriptionKey: 'feature_mis_description',
		descriptionDefault: 'Enable mathematical expressions in your scenes',
		feedbackLink: MATH_IN_SCENES_DISCORD_LINK,
		icon: 'fa-light fa-square-root-variable',
	},
	cs: {
		id: 'cs',
		nameKey: 'label_clone_device_config',
		nameDefault: 'Clone device config',
		descriptionKey: 'feature_cd_description',
		descriptionDefault:
			'Clone a device settings configuration or pre-saved template to another device',
		feedbackLink: CLONE_SETTINGS_DISCORD_LINK,
		icon: 'fa-light fa-copy',
	},
	nm: {
		id: 'nm',
		nameKey: 'label_net_metering_support',
		nameDefault: 'Net metering support',
		descriptionKey: 'feature_nm_description',
		descriptionDefault:
			'Enable net metering calculation and visualization in energy statistics for compatible Energy Meter devices (e.g. Shelly EM Gen3, Shelly Pro 3EM).',
		feedbackLink: NET_METERING_DISCORD_LINK,
		icon: 'fa-light fa-gauge',
	},
	pvs: {
		id: 'pvs',
		nameKey: 'label_pv_setup_energy_meters',
		nameDefault: 'PV Setup for energy meters',
		descriptionKey: 'feature_pvs_description',
		descriptionDefault:
			'Enable PV Setup in the Energy Meter settings to configure and monitor your photovoltaic system.',
		feedbackLink: PV_SETUP_DISCORD_LINK,
		icon: 'fa-light fa-solar-panel',
	},
};

export const isExperimentalFeatureEnabled = (featureId: ExperimentalFeatureId): boolean =>
	get(user_settings)?.experimental_features?.[featureId]?.enabled === 1;

/**
 * Persist a toggle to the server, update `user_settings`, and run any
 * per-feature side-effects (disable net-metering on off, delete PV setup on
 * off, etc). Returns `false` on failure so the caller can revert local state.
 */
export const toggleExperimentalFeature = async (
	featureId: ExperimentalFeatureId,
	willBeEnabled: boolean,
): Promise<boolean> => {
	try {
		const res = await postReq('/user/experimental_features', {
			set: featureId,
			val: willBeEnabled ? 1 : 0,
		});

		if (!res.isok) return false;

		user_settings.update((settings) => {
			if (!settings.experimental_features) settings.experimental_features = {};
			if (!settings.experimental_features[featureId]) {
				settings.experimental_features[featureId] = {};
			}
			settings.experimental_features[featureId].enabled = willBeEnabled ? 1 : 0;
			return settings;
		});

		if (featureId === 'nm' && !willBeEnabled) {
			disableAllNetMetering();
		} else if (featureId === 'pvs' && !willBeEnabled) {
			await deletePvSetup();
		}

		return true;
	} catch (error) {
		console.error('Error toggling experimental feature:', error);
		return false;
	}
};
